<template>
  <div>
    <h1 v-if="authStore.isAuthenticated">{{ authStore.user.displayName }}</h1>
    <h1 v-else>User Not Logged In</h1>
    <v-container fluid v-if="authStore.isAuthenticated">
      <v-row justify="center">
        <v-col md="6">
          <v-card class="pa-4 mb-8">
            <v-card-title>Upload File to Google Drive</v-card-title>
            <v-card-text>
              <v-file-input
                label="Select File"
                density="compact"
                variant="outlined"
                show-size
                prepend-icon="mdi-paperclip"
                v-model="selectedFile"
                clearable
              ></v-file-input>

              <v-btn
                color="primary"
                class="mt-4"
                @click="uploadFile"
                :disabled="!selectedFile || !googleAccessToken || isUploading"
                block
              >
                <v-icon start>mdi-upload</v-icon>
                Upload to Drive
              </v-btn>

              <v-alert
                v-if="uploadStatus"
                :type="uploadStatus.includes('success') ? 'success' : 'error'"
                class="mt-4"
                density="compact"
                text
              >
                <span v-html="uploadStatus"></span>
              </v-alert>

              <v-alert
                v-if="!googleAccessToken && authStore.isAuthenticated"
                type="warning"
                class="mt-4"
                density="compact"
                text
              >
                You need to sign in with Google to enable file uploads. Please
                sign out and sign in again to grant Google Drive permissions.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-divider class="my-8"></v-divider>

      <v-row justify="center">
        <v-col md="10">
          <v-card class="pa-4">
            <v-card-title>Files in Google Drive Folder</v-card-title>
            <v-card-text>
              <v-btn
                color="info"
                class="mb-4"
                @click="fetchDriveFiles"
                :loading="isFetchingFiles"
                :disabled="!googleAccessToken"
              >
                <v-icon start>mdi-refresh</v-icon>
                Refresh File List
              </v-btn>

              <v-data-table
                :headers="fileHeaders"
                :items="driveFiles"
                :loading="isFetchingFiles"
                class="elevation-1"
                item-key="id"
                no-data-text="No files found in this folder."
              >
                <template v-slot:item.name="{ item }">
                  <v-icon
                    small
                    :color="getFileIconColor(item.mimeType)"
                    class="mr-2"
                  >
                    {{ getFileIcon(item.mimeType) }}
                  </v-icon>
                  {{ item.name }}
                </template>

                <template v-slot:item.createdTime="{ item }">
                  {{ formatDate(item.createdTime) }}
                </template>

                <template v-slot:item.modifiedTime="{ item }">
                  {{ formatDate(item.modifiedTime) }}
                </template>

                <template v-slot:item.size="{ item }">
                  {{ formatBytes(item.size) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon
                    small
                    :href="item.webViewLink"
                    target="_blank"
                    color="blue"
                    class="mr-2"
                    v-if="item.webViewLink"
                  >
                    <v-icon>mdi-eye</v-icon>
                    <v-tooltip activator="parent" location="top"
                      >View in Drive</v-tooltip
                    >
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :href="item.webContentLink"
                    target="_blank"
                    color="green"
                    v-if="item.webContentLink"
                  >
                    <v-icon>mdi-download</v-icon>
                    <v-tooltip activator="parent" location="top"
                      >Download</v-tooltip
                    >
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue"; // No direct onMounted needed due to `watch` with `immediate: true`
import { useAuthStore } from "@/stores/auth";

const authStore = useAuthStore();

// --- Upload related reactive states ---
const selectedFile = ref(null);
const uploadStatus = ref("");
const isUploading = ref(false);

// --- File Listing related reactive states ---
const driveFiles = ref([]); // Array to hold the files fetched from Google Drive
const isFetchingFiles = ref(false); // Loading state for the file list operation

// Headers for the v-data-table
const fileHeaders = [
  { title: "Name", key: "name", sortable: true },
  { title: "Type", key: "mimeType", sortable: true },
  { title: "Size", key: "size", sortable: true },
  { title: "Created", key: "createdTime", sortable: true },
  { title: "Modified", key: "modifiedTime", sortable: true },
  { title: "Actions", key: "actions", sortable: false }, // Column for action buttons
];

// --- Access Token & Google Drive Folder ID ---
const googleAccessToken = computed(() => authStore.googleAccessToken);
// 💡 IMPORTANT: Replace this with your actual Google Drive Folder ID.
// This ID is specific to the folder where you want to upload and list files.
// Ensure this folder is shared correctly ("Anyone with the link" as "Editor").
const GOOGLE_DRIVE_FOLDER_ID = "1bzTLMNuTUtU-fjm6sibLbyQuIqH3rwEa";

// --- File Upload Logic ---
const uploadFile = async () => {
  if (!selectedFile.value) {
    uploadStatus.value = "Please select a file first.";
    return;
  }
  if (!googleAccessToken.value) {
    uploadStatus.value =
      "Not authenticated with Google Drive. Please sign in with Google.";
    return;
  }

  uploadStatus.value = "Uploading...";
  isUploading.value = true; // Disable button during upload

  const file = selectedFile.value; // The File object from v-file-input
  const fileName = file.name;
  const fileType = file.type || "application/octet-stream"; // Fallback MIME type

  console.log("File object from v-model:", file);
  console.log("Calculated fileName:", fileName);
  console.log("Calculated fileType:", fileType);

  // Metadata for the Google Drive file
  const metadata = {
    name: fileName,
    mimeType: fileType,
    parents: [GOOGLE_DRIVE_FOLDER_ID], // Specify the parent folder
  };
  console.log("Metadata sent:", metadata);

  // FormData for multipart upload
  const formData = new FormData();
  formData.append(
    "metadata",
    new Blob([JSON.stringify(metadata)], { type: "application/json" })
  );
  formData.append("file", file); // Append the actual file content

  try {
    const response = await fetch(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${googleAccessToken.value}`, // Authorization header
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Upload failed: ${response.status} - ${errorData.error.message}`
      );
    }

    const data = await response.json();
    console.log("File uploaded successfully, API response data:", data);

    const fileId = data.id;
    // 💡 Get webViewLink from response, or construct it if not provided directly
    const webViewLink =
      data.webViewLink || `https://drive.google.com/file/d/${fileId}/view`;

    // Update upload status with a clickable link
    uploadStatus.value = `Upload successful! File ID: ${fileId}. <a href="${webViewLink}" target="_blank">View File on Drive</a>`;

    selectedFile.value = null; // Clear the file input after successful upload

    // 💡 Refresh the file list after a successful upload to show the new file
    await fetchDriveFiles();
  } catch (error) {
    console.error("Error uploading file:", error);
    uploadStatus.value = `Upload failed: ${error.message}`;
  } finally {
    isUploading.value = false; // Re-enable the upload button
  }
};

// --- Fetch Drive Files Logic ---
// 💡 IMPORTANT: This function definition MUST come before any call to it (e.g., in the watch)
const fetchDriveFiles = async () => {
  if (!googleAccessToken.value) {
    console.warn("No Google Access Token available to fetch files.");
    driveFiles.value = []; // Clear files if no token
    return;
  }

  isFetchingFiles.value = true; // Set loading state for table
  driveFiles.value = []; // Clear previous list while fetching

  try {
    // 💡 MODIFIED QUERY: Add 'and trashed = false' to exclude trashed files
    const query = `'${GOOGLE_DRIVE_FOLDER_ID}' in parents and trashed = false`;

    // Request specific fields for each file to optimize response size
    const fields =
      "files(id, name, mimeType, webViewLink, webContentLink, createdTime, modifiedTime, size)";

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(
        query
      )}&fields=${encodeURIComponent(fields)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${googleAccessToken.value}`, // Authorize the request
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Failed to fetch files: ${response.status} - ${errorData.error.message}`
      );
    }

    const data = await response.json();
    console.log("Fetched Drive Files:", data.files);
    driveFiles.value = data.files || []; // Update the reactive array with the fetched files
  } catch (error) {
    console.error("Error fetching Drive files:", error);
    // You might want to display a user-friendly error message here
  } finally {
    isFetchingFiles.value = false; // Reset loading state
  }
};
// --- Watch for authenticated token to fetch files initially ---
// This watcher automatically calls `fetchDriveFiles` when `googleAccessToken` becomes available
// (e.g., on initial login or page refresh if already authenticated).
// `immediate: true` makes it run once on component mount if the token is already present.
watch(
  googleAccessToken,
  (newToken) => {
    if (newToken && authStore.isAuthenticated) {
      fetchDriveFiles(); // Fetch files when token is acquired and user is authenticated
    } else if (!newToken) {
      driveFiles.value = []; // Clear the file list if the token becomes unavailable
    }
  },
  { immediate: true }
);

// Add this to your dashboard.vue script setup section
watch(
  googleAccessToken,
  (newToken) => {
    console.log("Google Access Token:", newToken);
  },
  { immediate: true }
);

// --- Utility Functions for Data Display and Formatting ---

// Formats a date string into a readable format
const formatDate = (dateString) => {
  if (!dateString) return "";
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

// Formats file size in bytes into human-readable units (KB, MB, GB, etc.)
const formatBytes = (bytes, decimals = 2) => {
  if (bytes === undefined || bytes === null) return "";
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

// Returns an appropriate Material Design Icon based on MIME type
const getFileIcon = (mimeType) => {
  if (!mimeType) return "mdi-file";
  if (mimeType.startsWith("image/")) return "mdi-image";
  if (mimeType.startsWith("video/")) return "mdi-video";
  if (mimeType.startsWith("audio/")) return "mdi-audio";
  if (mimeType === "application/pdf") return "mdi-file-pdf-box";
  if (mimeType === "application/vnd.google-apps.document")
    return "mdi-file-document"; // Google Docs
  if (mimeType === "application/vnd.google-apps.spreadsheet")
    return "mdi-file-excel"; // Google Sheets
  if (mimeType === "application/vnd.google-apps.presentation")
    return "mdi-file-powerpoint"; // Google Slides
  if (mimeType === "application/vnd.google-apps.folder") return "mdi-folder"; // Google Drive folder
  // Add more specific types as needed
  return "mdi-file";
};

// Returns a color for the file icon based on MIME type
const getFileIconColor = (mimeType) => {
  if (!mimeType) return "grey";
  if (mimeType.startsWith("image/")) return "blue";
  if (mimeType.startsWith("video/")) return "purple";
  if (mimeType.startsWith("audio/")) return "orange";
  if (mimeType === "application/pdf") return "red";
  if (mimeType === "application/vnd.google-apps.document")
    return "blue-darken-2";
  if (mimeType === "application/vnd.google-apps.spreadsheet")
    return "green-darken-2";
  if (mimeType === "application/vnd.google-apps.presentation")
    return "orange-darken-2";
  if (mimeType === "application/vnd.google-apps.folder") return "brown";
  return "grey";
};

// const useGoogleDriveAPI = async () => {
//   const token = await authStore.getValidAccessToken();

//   if (!token) {
//     // User needs to login again
//     authStore.showSnackbar("กรุณาเข้าสู่ระบบใหม่", "warning");
//     return;
//   }

//   // Use token for API calls
//   const response = await fetch("https://www.googleapis.com/drive/v3/files", {
//     headers: {
//       Authorization: `Bearer ${token}`,
//     },
//   });
// };

onMounted(() => {
  if (!authStore.getBackendJwt()) {
    console.log("no token found, redirecting to login");
  }
});
</script>

<style scoped>
/* You can add component-specific styles here */
</style>
